import React, { useState, useEffect } from "react";

import { Box } from "@mui/material";
import { Clock } from "iconsax-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { format, parse, isValid, isFuture, addMinutes } from "date-fns";

import { Color } from "../../../../Form/Button";
import { ActionButtonProps } from "../../../ActionButtons";
import Modal from "../../../Modal";
import { Input } from "../../../../Form";
import { FormError } from "../../../../Form/FormError";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { NotificationToast } from "../../../Toast/NotificationToast";
import {
  ScheduleModalContainer,
  SectionTitle,
  FormRow,
} from "./styles";

interface ScheduleSendModalProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (scheduledDate: string) => void;
  communicationType: "email" | "sms" | "push";
  isLoading?: boolean;
}

const ScheduleSendModal: React.FC<ScheduleSendModalProps> = ({
  open,
  onClose,
  onSchedule,
  communicationType,
  isLoading = false,
}) => {
  const { t: translate } = useTranslation();
  const [sendDate, setSendDate] = useState<string>("");
  const [sendTime, setSendTime] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const getModalTitle = () => {
    if (communicationType === "email") {
      return translate("sendEmail") || "Send Email";
    }
    if (communicationType === "sms") {
      return translate("sendSMS") || "Send SMS";
    }
    return translate("pushNotification") || "Push Notification";
  };

  const schema = yup.object().shape({
    sendDate: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("not-past", translate("dateCannotBeInThePast") || "Date cannot be in the past", (value) => {
        if (!value) return true;
        const date = parse(value, "yyyy-MM-dd", new Date());
        if (!isValid(date)) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
      }),
    sendTime: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, translate("invalidTimeFormat") || "Invalid time format"),
  });

  useEffect(() => {
    if (!open) {
      setSendDate("");
      setSendTime("");
      setErrors([]);
    } else {
      // Set default to tomorrow at current time
      const tomorrow = addMinutes(new Date(), 1);
      setSendDate(format(tomorrow, "yyyy-MM-dd"));
      setSendTime(format(tomorrow, "HH:mm"));
    }
  }, [open]);

  const validateAndSchedule = async () => {
    try {
      await schema.validate(
        {
          sendDate,
          sendTime,
        },
        { abortEarly: false }
      );

      // Combine date and time and validate it's not in the past
      const dateTimeString = `${sendDate}T${sendTime}:00`;
      const scheduledDateTime = parse(dateTimeString, "yyyy-MM-dd'T'HH:mm:ss", new Date());

      if (!isValid(scheduledDateTime)) {
        setErrors([
          new yup.ValidationError(
            translate("invalidDateTime") || "Invalid date and time",
            sendDate,
            "sendDate"
          ),
        ]);
        return;
      }

      // Check if the combined date/time is in the future
      if (!isFuture(scheduledDateTime)) {
        setErrors([
          new yup.ValidationError(
            translate("scheduledTimeCannotBeInThePast") || "Scheduled time cannot be in the past",
            sendDate,
            "sendDate"
          ),
        ]);
        return;
      }

      // Convert to ISO string for backend
      const isoString = scheduledDateTime.toISOString();
      onSchedule(isoString);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }
    }
  };

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "scheduleSendButton",
    dataCyCancel: "cancelScheduleButton",
    textSave: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Clock size={20} />
        {translate("scheduleSend")}
      </Box>
    ),
    textCancel: translate("cancel"),
    customColorSave: Color.SUCCESS,
    customColorCancel: Color.CANCEL,
    saveDisabled: isLoading,
    onSave: validateAndSchedule,
    onCancel: onClose,
  };

  return (
    <Modal
      open={open}
      handleClose={onClose}
      showActionButtons
      title={getModalTitle()}
      aria-labelledby="schedule-send-modal-title"
      aria-describedby="schedule-send-modal-description"
      actionButtonProps={actionButtonProps}
      width={910}
    >
      <ScheduleModalContainer>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px" }}>
          <SectionTitle>{translate("scheduleSend")}</SectionTitle>

          <FormRow>
            <Box sx={{ flex: 1 }}>
              <Input
                type="date"
                label={translate("sendDate") || "Send date"}
                value={sendDate}
                onChange={(event) => {
                  setSendDate(event.target.value);
                  setErrors((prev) => prev.filter((err) => err.path !== "sendDate"));
                }}
                required
                min={format(new Date(), "yyyy-MM-dd")}
                error={fieldHasError("sendDate")}
                errorMessage={getErrorByField("sendDate")}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input
                type="time"
                label={translate("sendTime") || "Send time"}
                value={sendTime}
                onChange={(event) => {
                  setSendTime(event.target.value);
                  setErrors((prev) => prev.filter((err) => err.path !== "sendTime"));
                }}
                required
                error={fieldHasError("sendTime")}
                errorMessage={getErrorByField("sendTime")}
                fullWidth
              />
            </Box>
          </FormRow>

          {errors.length > 0 && <FormError isError={true}>{errors.map((error) => error.message).join(", ")}</FormError>}
        </Box>
      </ScheduleModalContainer>
    </Modal>
  );
};

export default ScheduleSendModal;

